import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { useCaption } from '@utils/providers/caption-context.tsx';
import { useAudio } from '@utils/providers/audio-context.tsx';
import { DocumentSubtitleSegmentV1 } from '@utils/types/editors/subtitles/subtitle.ts';
import {
  useContributeSegment,
  ContributeReadiness
} from '@utils/api/custom-models/use-contribute-segment.tsx';
import { useContributeWholeFile } from '@utils/api/custom-models/use-contribute-whole-file.tsx';
import Input from '@components/atoms/ui-kit/input.tsx';
import {
  MAX_CONTRIBUTION_MS,
  formatClipDuration,
  summarizeContribution
} from '@utils/editors/training-clip-windows.ts';
import { contributionResultMessage } from '@utils/editors/contribution-messages.ts';
import {
  AddToTrainingForm,
  ContributionMode,
  ContributionNote
} from '@components/editors/shared/add-to-training/add-to-training-form.tsx';
import { AddToTrainingResult } from '@components/editors/shared/add-to-training/add-to-training-result.tsx';
import { contributionErrorKey } from '@components/editors/shared/add-to-training/contribution-errors.ts';
import { useContributionModels } from '@components/editors/shared/add-to-training/use-contribution-models.ts';

const GUIDE_SEEN_KEY = 'cm-contribution-guide-seen';

type Props = {
  segment: DocumentSubtitleSegmentV1;
  filteredItems: DocumentSubtitleSegmentV1[];
  onClose: () => void;
  /** Fires once when a contribution succeeds. Parent decides whether to
   *  close the outer training modal and open the follow-up guide modal.
   *  Lifting the guide out of the panel avoids the previous
   *  "guide never opens" bug where the panel + guide unmounted together
   *  the moment the outer modal closed. */
  onSubmitted?: (payload: { isFirstContribution: boolean }) => void;
};

/** A caption's text box, as tall as its text. A fixed height scrolled long
 *  captions inside the box, so the customer could not see all of what they
 *  were about to contribute. */
const CaptionTextField = ({
  value,
  onChange
}: {
  value: string;
  onChange: (text: string) => void;
}) => {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    // scrollHeight excludes the border, which the box's height includes.
    const border = el.offsetHeight - el.clientHeight;
    el.style.height = `${el.scrollHeight + border}px`;
  }, [value]);
  return (
    <Input
      ref={ref as React.Ref<HTMLTextAreaElement>}
      multiline
      rows={1}
      size="small"
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{ overflow: 'hidden', resize: 'none' }}
    />
  );
};

export const CaptionAddToTrainingPanel = ({
  segment,
  filteredItems,
  onClose,
  onSubmitted
}: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const {
    session,
    document: captionDoc,
    documentResult,
    updateCaptionDocument,
    autoSave
  } = useCaption();
  const { pause, isPlaying, currentTime } = useAudio();

  const [mode, setMode] = useState<ContributionMode>('only');
  const [localTexts, setLocalTexts] = useState<Record<number, string>>({});
  const [result, setResult] = useState<ContributeReadiness | null>(null);

  const models = useContributionModels(
    session?.language_key ?? session?.language
  );

  // Built from the full document, not the search-filtered list: with a
  // search active the filtered run has gaps, which the API refuses as a
  // non-contiguous selection.
  const docSegments = captionDoc?.document ?? filteredItems;
  const anchorIndex = docSegments.findIndex(s => s.id === segment.id);

  // "only" is the picked segment; "up-to" everything from the start of the
  // document to it, which the API cuts into clips of at most 30 seconds.
  const windowSegs = useMemo(() => {
    if (anchorIndex < 0) return [];
    if (mode === 'only') return [docSegments[anchorIndex]];
    return docSegments.slice(0, anchorIndex + 1);
  }, [docSegments, anchorIndex, mode]);

  useEffect(() => {
    setLocalTexts(prev => {
      const next: Record<number, string> = {};
      for (const s of windowSegs) next[s.id] = prev[s.id] ?? s.text;
      return next;
    });
  }, [windowSegs]);

  const summary = useMemo(
    () =>
      summarizeContribution(
        windowSegs.map(s => ({ start: s.start, stop: s.stop }))
      ),
    [windowSegs]
  );
  // A whole file is the recording as far as the last line with text.
  const wholeMs = useMemo(() => {
    const worded = docSegments.filter(s => s.text?.trim());
    return worded.length ? worded[worded.length - 1].stop : 0;
  }, [docSegments]);
  const isWhole = mode === 'whole';
  // Nothing trainable: the only segment is longer than one 30s window.
  const overLimit =
    !isWhole && summary.clips === 0 && summary.hasOverlongSegment;
  const tooMuch = !isWhole && summary.ms > MAX_CONTRIBUTION_MS;

  // Auto-stop when playback passes the end of the contributed window.
  const windowEnd = windowSegs[windowSegs.length - 1]?.stop ?? 0;
  useEffect(() => {
    if (isPlaying && currentTime * 1000 >= windowEnd && windowEnd > 0) pause();
  }, [currentTime, isPlaying, windowEnd, pause]);

  // Keep the picked segment in view; ``nearest`` avoids yanking the dialog.
  const anchorRowRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    anchorRowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [mode, segment.id]);

  const documentId = documentResult?.result?.id ?? '';
  const sessionId = session?.session_id ?? '';
  const contributeSegment = useContributeSegment(sessionId, documentId);
  const contributeWholeFile = useContributeWholeFile(sessionId, documentId);
  const busy = contributeSegment.isLoading || contributeWholeFile.isLoading;
  const submitError = contributeSegment.error ?? contributeWholeFile.error;

  const handleContribute = () => {
    const modelId = models.selectedModelId;
    if (!modelId || !captionDoc || !session || !documentId) return;

    // Apply the dialog's text edits to the document and save silently, so the
    // API reads the corrected text. ``saveDocument`` would toast "Document
    // updated", which reads as if the click saved rather than contributed.
    updateCaptionDocument({
      ...captionDoc,
      document: captionDoc.document.map(s =>
        localTexts[s.id] !== undefined ? { ...s, text: localTexts[s.id] } : s
      )
    });
    autoSave();

    // Without it the dialog can look frozen between click and result.
    const toastId = toast.loading(t('contribution-submitting'));
    const handlers = {
      onSuccess: (data: ContributeReadiness) => {
        const isFirst = !localStorage.getItem(GUIDE_SEEN_KEY);
        if (isFirst) localStorage.setItem(GUIDE_SEEN_KEY, '1');
        const message = contributionResultMessage(
          data,
          models.selectedModelName
        );
        toast.success(t(message.key, message.values), { id: toastId });
        setResult(data);
        onSubmitted?.({ isFirstContribution: isFirst });
      },
      onError: () => toast.dismiss(toastId)
    };

    if (isWhole) {
      contributeWholeFile.mutate({ model_id: modelId }, handlers);
      return;
    }
    contributeSegment.mutate(
      {
        model_id: modelId,
        segment_ids: windowSegs.map(s => s.id),
        anchor_segment_id: segment.id
      },
      handlers
    );
  };

  if (result) {
    return (
      <AddToTrainingResult
        result={result}
        modelName={models.selectedModelName}
        onClose={onClose}
      />
    );
  }

  const notes: ContributionNote[] = [];
  if (overLimit)
    notes.push({ tone: 'warn', text: t('training-window-too-long') });
  if (tooMuch)
    notes.push({ tone: 'warn', text: t('contribution-long-selection') });
  if (models.isMultilingual && models.eligibleModels.length > 0) {
    notes.push({ tone: 'info', text: t('contribution-multilingual-hint') });
  }

  const modelName = models.selectedModelName;
  const summaryText = !modelName
    ? undefined
    : isWhole
      ? wholeMs > 0
        ? t('contribution-whole-adds', {
            duration: formatClipDuration(wholeMs),
            model: modelName
          })
        : undefined
      : !overLimit && summary.clips > 0
        ? t('contribution-adds', {
            duration: formatClipDuration(summary.ms),
            model: modelName
          })
        : undefined;

  const canSubmit =
    !!models.selectedModelId &&
    !!documentId &&
    !busy &&
    models.eligibleModels.length > 0 &&
    (isWhole ? wholeMs > 0 : !overLimit && summary.clips > 0);

  const accent = getColour('activeText');

  return (
    <AddToTrainingForm
      models={models}
      mode={mode}
      onModeChange={setMode}
      summary={summaryText}
      notes={notes}
      canSubmit={canSubmit}
      busy={busy}
      onSubmit={handleContribute}
      error={submitError ? t(contributionErrorKey(submitError)) : undefined}
    >
      {windowSegs.map(s => {
        const isAnchor = s.id === segment.id;
        return (
          <div
            key={s.id}
            ref={isAnchor ? anchorRowRef : undefined}
            className="flex gap-1.5"
          >
            <div
              className="w-0.5 shrink-0 rounded-full"
              style={{ backgroundColor: isAnchor ? accent : 'transparent' }}
            />
            <div className="flex-1">
              <CaptionTextField
                value={localTexts[s.id] ?? s.text}
                onChange={text =>
                  setLocalTexts(prev => ({ ...prev, [s.id]: text }))
                }
              />
            </div>
          </div>
        );
      })}
    </AddToTrainingForm>
  );
};
